import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { stripHtml } from "../utils/html";
import { PrivacyNotice } from "../components/PrivacyNotice";
import gsap from "gsap";
import { validateContact, submitContactRequest, type ContactErrors } from "../lib/contactValidation";
import { ga4 } from "../analytics/ga4";

/**
 * ContactWidget — widget flottante di CONTATTO.
 *
 * Rimpiazza la vecchia chat AI (ChatWidget) riutilizzandone il "guscio":
 * FAB in basso a destra + pannello slide-in + animazioni GSAP + header.
 * Il corpo è un form di contatto che invia a POST /api/v1/shop/contact
 * (stesso endpoint usato dalla pagina Contact.tsx).
 *
 * NB: la chat AI è conservata (commentata) in ChatWidget.tsx e può essere
 * riattivata in qualsiasi momento — vedi LocaleLayout.tsx.
 */

type FormState = "idle" | "sending" | "sent" | "error";

export function ContactWidget() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const location = useLocation();
  const cfg = useSiteConfig();
  const ct = useCmsText("contact");

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState<ContactErrors>({});

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const fabMountedRef = useRef(false);

  const brandDark = cfg.color_dark;
  const brandColor = cfg.color_primary;

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  // Close on route change (e.g. checkout navigation)
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Apertura da altri componenti (es. box "confezione personalizzata" nel carrello).
  // Stesso pattern di gb:open-cookie usato dal CookieConsent.
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("gb:open-contact", openHandler);
    return () => window.removeEventListener("gb:open-contact", openHandler);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (formState === "sending") return;                // guard anti doppio-submit
    const errs = validateContact(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;           // validazione condivisa
    setFormState("sending");
    try {
      await submitContactRequest({ ...form, source: "send_request_aside", locale: i18n.language || "it" });
      ga4.formSubmit("send_request_aside", "widget"); // solo su invio riuscito
      setFormState("sent");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setErrors({});
    } catch {
      setFormState("error");
      setTimeout(() => setFormState("idle"), 4000);
    }
  }

  // GSAP: Panel entrance / exit
  useEffect(() => {
    if (open) {
      setVisible(true);
    } else if (visible) {
      const tl = gsap.timeline({ onComplete: () => setVisible(false) });
      if (panelRef.current) {
        tl.to(panelRef.current, { x: "100%", duration: 0.3, ease: "power2.inOut" }, 0);
      }
      if (backdropRef.current) {
        tl.to(backdropRef.current, { opacity: 0, duration: 0.3, ease: "power2.inOut" }, 0);
      }
      tlRef.current = tl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // GSAP: entrance after visible mounts
  useEffect(() => {
    if (!visible || !open) return;
    const rafId = requestAnimationFrame(() => {
      const tl = gsap.timeline();
      if (backdropRef.current) {
        tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
      }
      if (panelRef.current) {
        tl.fromTo(panelRef.current, { x: "100%" }, { x: "0%", duration: 0.45, ease: "expo.out" }, 0);
      }
      const fields = panelRef.current?.querySelectorAll(".contact-field");
      if (fields && fields.length > 0) {
        tl.fromTo(
          fields,
          { y: 10, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.3, stagger: 0.05, ease: "power2.out" },
          0.2
        );
      }
      tlRef.current = tl;
    });
    return () => cancelAnimationFrame(rafId);
  }, [visible, open]);

  // GSAP: FAB scale bounce on first mount
  useEffect(() => {
    if (open || fabMountedRef.current) return;
    const rafId = requestAnimationFrame(() => {
      if (fabRef.current) {
        gsap.from(fabRef.current, { scale: 0, duration: 0.5, ease: "back.out(1.7)" });
        fabMountedRef.current = true;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [open]);

  // Cleanup
  useEffect(() => {
    return () => { tlRef.current?.kill(); };
  }, []);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid rgba(28,75,59,0.89)",
    borderRadius: 12,
    padding: "11px 14px",
    fontSize: 14,
    outline: "none",
    background: cfg.color_light_bg,
    color: cfg.color_dark,
    fontFamily: "inherit",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  };
  const onFieldFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = brandColor;
    e.currentTarget.style.boxShadow = `0 0 0 3px ${brandColor}14`;
  };
  const onFieldBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = "rgba(28,75,59,0.89)";
    e.currentTarget.style.boxShadow = "none";
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    color: cfg.color_dark,
    letterSpacing: 0.2,
  };

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        inset: 0,
        zIndex: 106,
        background: cfg.color_body_bg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: "translateX(100%)",
      }
    : {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 106,
        width: 420,
        background: cfg.color_body_bg,
        boxShadow: "-8px 0 32px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: "translateX(100%)",
      };

  return (
    <>
      {/* FAB — mail bubble */}
      {!open && !visible && (
        <button
          ref={fabRef}
          onClick={() => setOpen(true)}
          aria-label={ct("formTitle") || t("chat.open") || "Contattaci"}
          className="contact-fab"
          style={{
            position: "fixed",
            bottom: isMobile ? 20 : 28,
            right: isMobile ? 20 : 28,
            zIndex: 105,
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: brandDark,
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 6px 28px rgba(0,0,0,0.25)",
            transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s",
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z" />
            <path d="M3 6l9 7 9-7" />
          </svg>
        </button>
      )}

      {/* Backdrop */}
      {(open || visible) && !isMobile && (
        <div
          ref={backdropRef}
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 105,
            background: "rgba(0,0,0,0.08)",
            opacity: 0,
          }}
        />
      )}

      {/* Contact Panel */}
      {(open || visible) && (
        <div ref={panelRef} style={panelStyle}>
          {/* Header */}
          <div
            style={{
              padding: isMobile ? "14px 16px" : "16px 24px",
              paddingTop: isMobile ? "calc(env(safe-area-inset-top, 14px) + 4px)" : 16,
              borderBottom: "1px solid rgba(28,75,59,0.89)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: cfg.color_body_bg,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${cfg.color_light_bg} 0%, ${cfg.color_body_bg} 100%)`,
                border: "1px solid rgba(28,75,59,0.89)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <span style={{ fontSize: 16, fontWeight: 500, color: brandDark, letterSpacing: "-0.03em", lineHeight: 1 }}>GB</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: brandDark, letterSpacing: "-0.01em" }}>
                  {ct("formTitle") || cfg.brand_name}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: "rgba(28,75,59,0.86)" }}>
                    {ct("overline") || cfg.contact_email || "info@granborgo.com"}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t("chat.close") || "Chiudi"}
              style={{
                background: "none",
                border: "1px solid rgba(28,75,59,0.89)",
                color: "rgba(28,75,59,0.86)",
                width: 32,
                height: 32,
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "border-color 0.2s, color 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#999"; e.currentTarget.style.color = cfg.color_dark; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.89)"; e.currentTarget.style.color = "#999"; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "22px 18px" : "26px 24px",
              background: cfg.color_light_bg,
            }}
          >
            {formState === "sent" ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, textAlign: "center", padding: "0 16px" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: cfg.color_dark, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <p style={{ fontFamily: cfg.font_heading, fontSize: 22, fontWeight: 400, color: cfg.color_dark, margin: "0 0 8px" }}>
                  {ct("sentTitle") || "Messaggio inviato"}
                </p>
                <p style={{ fontSize: 14, color: "rgba(28,75,59,0.6)", margin: "0 0 24px" }}>
                  {ct("sentSubtitle") || "Ti risponderemo al più presto."}
                </p>
                <button
                  onClick={() => setFormState("idle")}
                  style={{
                    background: "none",
                    border: "1px solid rgba(28,75,59,0.89)",
                    color: cfg.color_dark,
                    padding: "10px 22px",
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 50,
                    cursor: "pointer",
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  {ct("send") ? "OK" : "OK"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Name */}
                <div className="contact-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={labelStyle}>
                    {ct("name") || "Nome"}<span style={{ color: "rgba(28,75,59,0.4)" }}> *</span>
                  </label>
                  <input type="text" required value={form.name} onChange={set("name")} style={inputStyle} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                </div>

                {/* Company */}
                <div className="contact-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={labelStyle}>{ct("company") || "Azienda"}</label>
                  <input type="text" value={form.company} onChange={set("company")} style={inputStyle} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                </div>

                {/* Email */}
                <div className="contact-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={labelStyle}>
                    {ct("email") || "Email"}<span style={{ color: "rgba(28,75,59,0.4)" }}> *</span>
                  </label>
                  <input type="email" required value={form.email} onChange={set("email")} style={inputStyle} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                  {errors.email && <span style={{ fontSize: 12, color: "#c0392b" }}>{ct("errEmail") || "Inserisci un indirizzo email valido."}</span>}
                </div>

                {/* Phone */}
                <div className="contact-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={labelStyle}>{ct("phone") || "Telefono"}</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} style={inputStyle} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                  {errors.phone && <span style={{ fontSize: 12, color: "#c0392b" }}>{ct("errPhone") || "Inserisci un numero di telefono valido."}</span>}
                </div>

                {/* Message */}
                <div className="contact-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={labelStyle}>
                    {ct("message") || "Messaggio"}<span style={{ color: "rgba(28,75,59,0.4)" }}> *</span>
                  </label>
                  <textarea
                    required rows={4} value={form.message} onChange={set("message")}
                    placeholder={stripHtml(ct("messagePlaceholder") || "")}
                    style={{ ...inputStyle, resize: "vertical", minHeight: 110 }}
                    onFocus={onFieldFocus} onBlur={onFieldBlur}
                  />
                </div>

                {formState === "error" && (
                  <p style={{ fontSize: 13, color: "#c0392b", margin: 0 }}>
                    {ct("errorMsg") || "Si è verificato un errore. Riprova."}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={formState === "sending"}
                  className="contact-field"
                  style={{
                    background: cfg.color_dark,
                    color: "#fff",
                    border: "none",
                    padding: "15px 24px",
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 50,
                    cursor: formState === "sending" ? "not-allowed" : "pointer",
                    transition: "opacity .2s, background .2s",
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    marginTop: 4,
                    opacity: formState === "sending" ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => { if (formState !== "sending") e.currentTarget.style.opacity = "0.85"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  {formState === "sending" ? "..." : (ct("send") || "Invia")}
                </button>

                {/* Informativa GDPR (finalità A) — link privacy policy; nessuna checkbox obbligatoria */}
                <div className="contact-field">
                  <PrivacyNotice variant="contact" />
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <style>{`
        .contact-fab:hover { transform: scale(1.1) !important; box-shadow: 0 8px 36px rgba(0,0,0,0.3) !important; }
        .contact-fab:active { transform: scale(0.95) !important; }
      `}</style>
    </>
  );
}
